"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Mission() {
  const prefersReduced = useReducedMotion();
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mediaQuery.matches || Boolean(prefersReduced));
    const listener = (event: MediaQueryListEvent) => {
      setReduceMotion(event.matches);
    };
    mediaQuery.addEventListener("change", listener);
    return () => mediaQuery.removeEventListener("change", listener);
  }, [prefersReduced]);
  return <section id="banner-giới-thiệu-sứ-mệnh" data-nav="dark" className="relative min-h-[630px] h-[99vh] max-h-[960px] w-full overflow-hidden bg-background">
      {/* Background HTML Video */}
      <div className="absolute inset-0 z-0">
        <video src="https://cegtes.vn/wp-content/uploads/2026/09/download.mp4" autoPlay loop muted playsInline className="h-full w-full object-cover object-center" />
      </div>

      {/* Permanent Directional Scrim Overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 bg-[#0F2A44]/55" />

      {/* Topographic subtle line texture accent */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 opacity-10 mix-blend-overlay bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-foreground via-transparent to-transparent" />

      {/* Hero Typography & Mission Content Container */}
      <div className="relative z-20 mx-auto flex h-full max-w-7xl flex-col justify-center px-6 py-20 lg:px-12">
        <div className="max-w-2xl lg:max-w-3xl">
          {/* Eyebrow Line */}
          <motion.p initial={reduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.38,
          ease: [0.16, 1, 0.3, 1]
        }} className="uppercase text-primary-foreground tracking-widest text-xs sm:text-sm font-medium mb-3">
            VIETNAM ASSOCIATION OF ENVIRONMENTAL GEOGRAPHY · VAEG
          </motion.p>

          {/* Main Title (Dictated Verbatim) */}
          <motion.h1 initial={reduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.38,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl md:text-5xl lg:text-5xl whitespace-nowrap">
            Hội Địa lý môi trường Việt Nam
          </motion.h1>

          {/* Mission Subtitle Statement */}
          <motion.p initial={reduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.38,
          delay: 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="mt-5 text-base leading-relaxed text-primary-foreground/90 sm:text-lg md:text-xl md:leading-relaxed font-normal">
            Tập hợp và phát huy năng lực đội ngũ nhà khoa học, chuyên gia trong lĩnh vực địa lý và môi trường; đẩy mạnh nghiên cứu ứng dụng, bảo vệ tài nguyên sinh thái và đồng hành cùng tiến trình phát triển bền vững quốc gia.
          </motion.p>

          {/* Key Mandate Pillars Strip */}
          <motion.div initial={reduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.38,
          delay: 0.12,
          ease: [0.16, 1, 0.3, 1]
        }} className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3 border-y border-primary-foreground/20 py-4 text-xs md:text-sm text-primary-foreground/80">
            <div className="flex flex-col">
              <span className="font-semibold text-primary-foreground">Khoa học Địa lý Phong thủy</span>
              <span className="text-primary-foreground/70">Dịch lý, thiên văn lịch pháp, Hán Nôm cổ học</span>
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-primary-foreground">Môi trường & Biến đổi khí hậu</span>
              <span className="text-primary-foreground/70">Cộng đồng thích ứng, năng lượng xanh, ESG</span>
            </div>
            <div className="col-span-2 sm:col-span-1 flex flex-col">
              <span className="font-semibold text-primary-foreground">Hội thảo & Đào tạo</span>
              <span className="text-primary-foreground/70">Kết nối hội viên, chuyên gia</span>
            </div>
          </motion.div>

          {/* Primary Action Button (Dictated Verbatim) */}
          <motion.div initial={reduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.38,
          delay: 0.16,
          ease: [0.16, 1, 0.3, 1]
        }} className="mt-8 flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground border border-primary-foreground/30 shadow-md font-semibold px-7 py-3 rounded-xs transition-colors">
              <Link to="/AboutPage" className="inline-flex items-center gap-2">
                <span>Tìm hiểu về Hội</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="bg-transparent hover:bg-white/10 text-white border border-white font-semibold px-6 py-3 rounded-xs transition-colors">
              <Link to="/OrganizationStructurePage">Cơ cấu tổ chức</Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>;
}